import { trackSystemError } from '@/lib/alerting';
import { STORAGE_KEYS } from '@/lib/storage-keys';

/**
 * Web Storage that never throws. Also a valid zustand `StateStorage`, so persisted stores
 * use it directly: `createJSONStorage(() => safeLocalStorage)`.
 */
type SafeStorage = {
  getItem: (key: string) => string | null;
  /** When the save fails, callers keep working from in-memory state. */
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

// One report per key per page load: persisted stores write on every change.
const reportedFailureKeys = new Set<string>();

function reportWriteFailure(key: string, error: unknown) {
  if (reportedFailureKeys.has(key)) {
    return;
  }
  reportedFailureKeys.add(key);
  trackSystemError(error, { type: 'browser-storage-write', extra: { key } });
}

function createSafeStorage(getStorage: () => Storage): SafeStorage {
  const resolve = (): Storage | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return getStorage();
    } catch {
      // Some browsers throw SecurityError on access when storage is blocked.
      return null;
    }
  };

  return {
    getItem: (key) => {
      try {
        return resolve()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      const storage = resolve();
      if (!storage) {
        return;
      }
      try {
        storage.setItem(key, value);
        return;
      } catch {
        // A full quota. zustand `persist` applies `set` before it saves, so letting this
        // throw aborted callers midway: "Add to review" kept its text and the next click
        // duplicated the comment.
      }
      try {
        // The query cache only mirrors server data: drop it to make room, then retry once.
        storage.removeItem(STORAGE_KEYS.QUERY_CACHE);
        storage.setItem(key, value);
      } catch (error) {
        reportWriteFailure(key, error);
      }
    },
    removeItem: (key) => {
      try {
        resolve()?.removeItem(key);
      } catch {
        // The stale value stays until the next successful write or removal.
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage(() => window.localStorage);
export const safeSessionStorage = createSafeStorage(() => window.sessionStorage);
